import React from "react";
import { Grid, Typography } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  CollapsiblePanel,
  ShowValue,
  SubHeader
} from "src/apps/commons/dataDisplays";
import { FileUploadImage } from "src/apps/commons/fileUploads";
import { SimpleSubTable } from "src/apps/commons/form";
import { formatDate, formatDateTime } from "src/utils";
import { repeatUnitTypeMap } from "src/utils/enum";
import {
  IUserProductBrief,
  IUserProductContainer,
  IUserProductReminderBrief
} from "../types";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listTextPrimary: {
      color: theme.palette.text.primary
    },
    listTextSecondary: {
      color: theme.palette.text.secondary
    },
    listTextTertiary: {
      color: theme.palette.text.secondary
    },
    image: {
      maxWidth: theme.breakpoints.width("md"),
      "& > .MuiAvatar-root": {
        height: 200
      }
    }
  })
);

interface Props {
  data: IUserProductContainer;
}

const UserProductInfo: React.FC<Props> = props => {
  const classes = useStyles();
  const { data } = props;
  const { product, userAttachmentList, userReminderList } = data;
  const { receipt } = product;

  const categoryShow = (data: IUserProductBrief) => {
    const { categoryLv1, categoryLv2, categoryLv3 } = data;
    if (!categoryLv1 && !categoryLv2 && !categoryLv3) {
      return undefined;
    }
    return (
      <div>
        {categoryLv1?.label && (
          <div>
            <Typography className={classes.listTextPrimary}>
              L1: {categoryLv1.label}
            </Typography>
          </div>
        )}
        {categoryLv2?.label && (
          <div>
            <Typography className={classes.listTextSecondary}>
              L2: {categoryLv2.label}
            </Typography>
          </div>
        )}
        {categoryLv3?.label && (
          <div>
            <Typography className={classes.listTextTertiary}>
              L3: {categoryLv3.label}
            </Typography>
          </div>
        )}
      </div>
    );
  };
  const brandShow = (data: IUserProductBrief) => {
    const { brand, customBrandName } = data;
    return brand ? (
      brand.label
    ) : customBrandName ? (
      <>
        {customBrandName}{" "}
        <Typography variant="caption" color="primary">
          (กรอกเอง)
        </Typography>
      </>
    ) : undefined;
  };

  return (
    <CollapsiblePanel title="รายละเอียดสินค้า" icon="info">
      <Grid container spacing={2}>
        <Grid item container spacing={2} xs={12}>
          <Grid
            item
            container
            spacing={1}
            xs={12}
            md={6}
            alignContent="flex-start"
          >
            <Grid item xs={12}>
              <ShowValue label="ชื่อสินค้า" value={product.productName} />
            </Grid>
            <Grid item xs={12}>
              <ShowValue label="หมวดหมู่สินค้า" value={categoryShow(product)} />
            </Grid>
            <Grid item xs={12}>
              <ShowValue label="แบรนด์" value={brandShow(product)} />
            </Grid>
            <Grid item xs={12}>
              <ShowValue label="โน้ต" value={product.productRemark} />
            </Grid>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <ShowValue
                label="ต้องการให้ช่วยลงทะเบียนการรับประกัน"
                value={product.requestWarranty ? "ใช่" : undefined}
              />
            </Grid>
            <Grid item xs={12} />
            <Grid item xs={12}>
              <ShowValue
                label="สร้างเมื่อ"
                value={formatDateTime(product.userCreatedAt)}
              />
            </Grid>
            <Grid item xs={12}>
              <ShowValue
                label="แก้ไขล่าสุดเมื่อ"
                value={formatDateTime(product.userUpdatedAt)}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            {product.productImageId ? (
              <FileUploadImage
                label="รูปถ่ายสินค้า"
                fileId={product.productImageId}
                className={classes.image}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                ไม่มีรูปถ่ายสินค้า
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <SubHeader title="ข้อมูลใบเสร็จ" marginTop />
        </Grid>
        <Grid item container xs={12}>
          <Grid
            item
            container
            spacing={1}
            xs={12}
            md={6}
            alignContent="flex-start"
          >
            <Grid item xs={12}>
              <ShowValue label="เลขที่ใบเสร็จ" value={receipt?.receiptNo} />
            </Grid>
            <Grid item xs={12}>
              <ShowValue label="ร้านค้า" value={receipt?.sellerName} />
            </Grid>
            <Grid item xs={12}>
              <ShowValue
                label="วันที่ซื้อ"
                value={
                  receipt?.purchasedDate && formatDate(receipt.purchasedDate)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <ShowValue label="หมายเหตุ" value={receipt?.remark} />
            </Grid>
          </Grid>
          <Grid item container xs={12} md={6}>
            <Grid item xs={12}>
              {receipt?.imageId ? (
                <FileUploadImage
                  label="รูปถ่ายใบเสร็จ"
                  fileId={receipt.imageId}
                  className={classes.image}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  ไม่มีรูปถ่ายใบเสร็จ
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>

        {(product.refData || product.serialNo) && (
          <>
            <Grid item xs={12}>
              <SubHeader title="ข้อมูลจากการสแกน QR/Barcode" marginTop />
            </Grid>
            <Grid item container xs={12} md={6}>
              <Grid
                item
                container
                spacing={1}
                xs={12}
                alignContent="flex-start"
              >
                <Grid item xs={12}>
                  <ShowValue
                    label="QR/Barcode ที่สแกน"
                    value={product.refData}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ShowValue label="Serial No." value={product.serialNo} />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <SubHeader
            title={`การแจ้งเตือน (${userReminderList.length})`}
            marginTop
          />
        </Grid>
        <Grid item xs={12} lg={8}>
          <SimpleSubTable<IUserProductReminderBrief>
            data={userReminderList}
            showIndex
            columns={[
              {
                label: "เรื่อง",
                renderCell: req => req.title
              },
              {
                label: "วันที่เตือนครั้งถัดไป",
                width: 200,
                renderCell: req =>
                  req.remindAt ? formatDateTime(req.remindAt) : "ไม่ระบุ"
              },
              {
                label: "การแจ้งเตือนซ้ำ",
                width: 200,
                renderCell: req =>
                  req.repeatEvery && req.repeatUnit
                    ? `ทุก ๆ ${req.repeatEvery} ${
                        repeatUnitTypeMap[req.repeatUnit]
                      }`
                    : "ไม่ระบุ"
              }
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <SubHeader
            title={`ไฟล์แนบ (${userAttachmentList.length})`}
            marginTop
          />
        </Grid>
        {userAttachmentList.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              ไม่มีไฟล์แนบ
            </Typography>
          </Grid>
        )}
        <Grid item container spacing={2} xs={12}>
          {userAttachmentList.map((id, idx) => (
            <Grid item xs={6} md={4} lg={2} key={id}>
              <FileUploadImage
                label={`รูป ${idx + 1}`}
                fileId={id}
                className={classes.image}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </CollapsiblePanel>
  );
};

export default UserProductInfo;
